import { useEffect, useState, useRef, useCallback } from 'react';
import { pivotIds, pivotUrl } from '../lib/prefetchPaths';
import { MobileHeader, HeaderAction, MobileToolbar, Chip } from '../components/MobileHeader';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Plus, MapPin, Calendar, Users, ChevronRight, X, Save, TrendingUp, ChevronDown, Copy, Layers, Archive } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import { api } from '../api';
import { cn } from '../lib/utils';
import { TimePicker24h } from '../components/TimePicker24h';

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const LEVELS = ['Débutant', 'Evolutif', 'Intermédiaire', 'Avancé', 'Tous niveaux', 'Open'];
const COLORS = ['#0288D1', '#26A69A', '#E91E8C', '#FF6F00', '#43A047', '#7B1FA2', '#F44336', '#607D8B', '#FF7043', '#00ACC1'];
const fmtDuration = (m) => `${Math.floor(m / 60)}h${m % 60 ? m % 60 : ''}`;

const normTxt = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function GroupClasses() {
  const { t, lang } = useLang();
  const { effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [locations, setLocations] = useState([]);
  const [inactivesOpen, setInactivesOpen] = useState(false);
  const [filterLocation, setFilterLocation] = useState(
    () => sessionStorage.getItem('ds_location_filter') || 'all'
  );
  const [groupByLocation, setGroupByLocation] = useState(false);
  const [courseQuery, setCourseQuery] = useState('');  // recherche (loupe de la barre du titre)
  const [duplicating, setDuplicating] = useState('');

  // Persiste le filtre lieu en session + helper
  const setFilter = (val) => {
    setFilterLocation(val);
    sessionStorage.setItem('ds_location_filter', val);
  };

  const handleDuplicate = async (e, courseId) => {
    e.stopPropagation();
    if (!window.confirm('Dupliquer ce cours (copie du nom, prix, inscrits) ?')) return;
    setDuplicating(courseId);
    try {
      const result = await api.post(`/group-courses/${courseId}/duplicate`);
      await fetchCourses();
      navigate(`/group-classes/${result.id}`);
    } catch (err) {
      alert('Erreur duplication : ' + (err.detail || err.message));
    } finally {
      setDuplicating('');
    }
  };

  const DAYS = lang === 'fr' ? DAYS_FR : DAYS_EN;

  const handleToggleActive = async (e, course) => {
    e.stopPropagation();
    try {
      await api.patch(`/group-courses/${course.id}/active`, { is_active: !course.is_active });
      fetchCourses();
    } catch { toast.error('Erreur'); }
  };

  const fetchCourses = async () => {
    const [c, l] = await Promise.all([api.get('/group-courses').catch(() => []), api.get('/locations').catch(() => [])]);
    setCourses(c);
    setLocations(l);
    // v2.18 : les pages des cours actifs se préparent en arrière-plan (ouverture quasi immédiate)
    c.filter(x => x.is_active !== false).forEach(x => {
      api.get(`/group-courses/${x.id}`).catch(() => {});
      api.get(`/payments?group_course_id=${x.id}`).catch(() => {});
    });
    // Défaut = Crissier au premier chargement (si pas encore sauvegardé en session)
    if (!sessionStorage.getItem('ds_location_filter') && l.length > 0) {
      const crissier = l.find(loc =>
        loc.city?.toLowerCase() === 'crissier' ||
        loc.venue_name?.toLowerCase().includes('crissier')
      );
      const defaultId = crissier ? crissier.id : 'all';
      sessionStorage.setItem('ds_location_filter', defaultId);
      setFilterLocation(defaultId);
    }
    setLoading(false);
  };

  useEffect(() => { fetchCourses(); }, []);

  const inactiveCount = courses.filter(c => c.is_active === false).length;
  const toggleInactives = () => {
    const open = !inactivesOpen;
    setInactivesOpen(open);
    // si le filtre de lieu cache tous les cours inactifs, on montre tous les lieux (sinon le bouton n'afficherait rien)
    if (open && filterLocation !== 'all' && !courses.some(c => c.is_active === false && c.location_id === filterLocation)) {
      setFilterLocation('all');
    }
    if (open) setTimeout(() => document.getElementById('inactive-courses')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">{t('common.loading')}</div>;

  return (
    <div className="max-w-5xl mx-auto" data-testid="group-classes-page">
      <MobileHeader title="Collectifs" count={courses.filter(c => c.is_active !== false).length}
        search={{ value: courseQuery, onChange: setCourseQuery, placeholder: 'Chercher un cours…', scope: 'courses' }}
        actions={<>
          {inactiveCount > 0 && <HeaderAction icon={Archive} label={`Cours inactifs (${inactiveCount})`} active={inactivesOpen}
            badge={inactiveCount} onClick={toggleInactives} testId="show-inactive-btn-mobile" />}
          {effectiveRole === 'admin' && <HeaderAction icon={Plus} label={t('groupClasses.add')} primary onClick={() => setShowModal(true)} testId="add-group-course-btn-mobile" />}
        </>} />

      {courses.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm">
          <BookOpen size={40} className="text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Aucun cours collectif</p>
          <button onClick={() => setShowModal(true)} className="mt-3 text-[#0288D1] text-sm font-semibold hover:underline">
            Créer votre premier cours →
          </button>
        </div>
      ) : (() => {
        const matchQuery = (c) => !courseQuery.trim() || normTxt(`${c.name} ${c.location?.venue_name || ''}`).includes(normTxt(courseQuery.trim()));
        const activeCourses = courses
          .filter(c => c.is_active !== false)
          .filter(c => filterLocation === 'all' || c.location_id === filterLocation)
          .filter(matchQuery);
        const inactiveCourses = courses
          .filter(c => c.is_active === false)
          .filter(c => filterLocation === 'all' || c.location_id === filterLocation)
          .filter(matchQuery);

        // Lieux uniques parmi tous les cours
        const uniqueLocations = Array.from(
          new Map(courses.filter(c => c.location_id).map(c => [c.location_id, c.location])).entries()
        ).map(([id, loc]) => ({ id, ...loc }));

        const CourseCard = ({ course }) => (
          <div
            key={course.id}
            onClick={() => navigate(`/group-classes/${course.id}`)}
            className={cn(
              'bg-white rounded-2xl p-3.5 lg:p-5 border shadow-sm hover:shadow-md cursor-pointer active:scale-[0.98] transition-all',
              course.is_active === false ? 'border-slate-100 opacity-60' : 'border-slate-100'
            )}
            data-testid="group-course-card"
          >
            {/* Mobile : tuile compacte (3 lignes) */}
            <div className="mobile-only">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: course.color || '#0288D1' }} />
                <h3 className="flex-1 min-w-0 font-bold text-slate-900 font-manrope text-base truncate">{course.name}</h3>
                <button
                  onClick={e => handleToggleActive(e, course)}
                  className={cn('shrink-0 px-2 py-0.5 rounded-full text-xs font-bold border',
                    course.is_active !== false
                      ? 'bg-[#26A69A]/10 text-[#26A69A] border-[#26A69A]/20'
                      : 'bg-slate-100 text-slate-400 border-slate-200')}>
                  {course.is_active !== false ? 'Actif' : 'Inactif'}
                </button>
                <ChevronRight size={16} className="text-slate-300 shrink-0" />
              </div>
              <div className="mt-1 flex items-center gap-x-3 text-sm text-slate-500 min-w-0">
                <span className="flex items-center gap-1 shrink-0"><Calendar size={13} className="text-slate-400" />{DAYS[course.day_of_week]} {course.start_time}</span>
                <span className="flex items-center gap-1 min-w-0"><MapPin size={13} className="text-slate-400 shrink-0" /><span className="truncate">{course.location?.venue_name}</span></span>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-sm">
                <span className="text-slate-700"><b className="font-extrabold">{course.total_enrolled || 0}</b> <span className="text-slate-400">élèves</span></span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0288D1]" /><b className="text-slate-700">{course.active_leader_count ?? course.leader_count ?? 0}</b><span className="text-slate-400 text-xs">L</span></span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-pink-400" /><b className="text-slate-700">{course.active_follower_count ?? course.follower_count ?? 0}</b><span className="text-slate-400 text-xs">F</span></span>
                <span className="text-slate-400">· {course.session_count || 0} séances</span>
                <button
                  onClick={e => handleDuplicate(e, course.id)}
                  disabled={duplicating === course.id}
                  aria-label="Dupliquer ce cours"
                  className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 border border-slate-100 active:bg-slate-50">
                  <Copy size={14} />
                </button>
              </div>
            </div>

            {/* PC : tuile détaillée */}
            <div className="mobile-hidden">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: course.color || '#0288D1' }} />
                  <h3 className="font-bold text-slate-900 font-manrope text-lg truncate">{course.name}</h3>
                </div>
                {course.level && course.level.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(Array.isArray(course.level) ? course.level : [course.level]).map(l => (
                      <span key={l} className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{l}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0 ml-2">
                <button
                  onClick={e => handleToggleActive(e, course)}
                  className={cn('px-2.5 py-1 rounded-full text-xs font-bold transition-all border',
                    course.is_active !== false
                      ? 'bg-[#26A69A]/10 text-[#26A69A] border-[#26A69A]/20 hover:bg-[#26A69A]/20'
                      : 'bg-slate-100 text-slate-400 border-slate-200 hover:bg-slate-200')}
                  data-testid={`toggle-active-${course.id}`}>
                  {course.is_active !== false ? 'Actif' : 'Inactif'}
                </button>
                <ChevronRight size={16} className="text-slate-300" />
              </div>
            </div>
            <div className="space-y-1.5 text-sm text-slate-600 mb-4">
              <div className="flex items-center gap-2">
                <Calendar size={13} className="text-slate-400" />
                {DAYS[course.day_of_week]} · {course.start_time} ({fmtDuration(course.duration_minutes)})
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={13} className="text-slate-400" />
                {course.location?.venue_name} · {course.location?.city}
              </div>
            </div>
            <div className="flex items-center gap-4 pt-3 border-t border-slate-100">
              <div className="text-center">
                <div className="text-xl font-extrabold text-slate-900 font-manrope">{course.total_enrolled || 0}</div>
                <div className="text-xs text-slate-400">actifs</div>
              </div>
              <div className="flex gap-4 text-sm flex-1">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#0288D1]" />
                  <span className="font-semibold text-slate-700">{course.active_leader_count ?? course.leader_count ?? 0}</span>
                  <span className="text-slate-400 text-xs">L</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-pink-400" />
                  <span className="font-semibold text-slate-700">{course.active_follower_count ?? course.follower_count ?? 0}</span>
                  <span className="text-slate-400 text-xs">F</span>
                </div>
                {(course.inactive_count ?? 0) > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <span className="font-semibold text-slate-400">{course.inactive_count}</span>
                    <span className="text-slate-300 text-xs">inactifs</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={e => handleDuplicate(e, course.id)}
                  disabled={duplicating === course.id}
                  title="Dupliquer ce cours"
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-slate-400 hover:text-[#0288D1] hover:bg-[#E1F5FE] transition-colors border border-slate-100"
                  data-testid={`duplicate-course-${course.id}`}>
                  <Copy size={12} />
                  {duplicating === course.id ? '…' : 'Dupliquer'}
                </button>
                <div className="text-right">
                  <div className="text-sm font-bold text-slate-700">{course.session_count || 0}</div>
                  <div className="text-xs text-slate-400">séances</div>
                </div>
              </div>
            </div>
            </div>
          </div>
        );

        // Grouper par lieu si activé
        const renderGrid = (courseList) => {
          if (!groupByLocation || uniqueLocations.length < 2) {
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 lg:gap-4">{courseList.map(c => <CourseCard key={c.id} course={c} />)}</div>;
          }
          const byLoc = {};
          courseList.forEach(c => {
            const lid = c.location_id || 'other';
            if (!byLoc[lid]) byLoc[lid] = [];
            byLoc[lid].push(c);
          });
          return Object.entries(byLoc).map(([lid, list]) => {
            const loc = uniqueLocations.find(l => l.id === lid);
            return (
              <div key={lid} className="space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin size={13} className="text-slate-400" />
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                    {loc?.venue_name || 'Lieu non défini'}
                    {loc?.city ? ` · ${loc.city}` : ''}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 lg:gap-4">{list.map(c => <CourseCard key={c.id} course={c} />)}</div>
              </div>
            );
          });
        };

        return (
          <div className="space-y-5">
            {/* Filtres lieu + groupement */}
            {uniqueLocations.length > 1 && (
              <MobileToolbar className="!mb-0">
                {/* v2.29 : descendre directement au tableau CA (en bas de la page) */}
                <Chip icon={TrendingUp} onClick={() => document.getElementById('revenue-pivot')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  testId="jump-to-revenue">CA</Chip>
                <Chip active={filterLocation === 'all'} onClick={() => setFilter('all')} color="#334155">Tous les lieux</Chip>
                {uniqueLocations.map(loc => (
                  <Chip key={loc.id} active={filterLocation === loc.id} icon={MapPin}
                    onClick={() => setFilter(filterLocation === loc.id ? 'all' : loc.id)}>{loc.venue_name}</Chip>
                ))}
                <span className="shrink-0 w-px h-6 bg-slate-200" />
                <Chip active={groupByLocation} icon={Layers} onClick={() => setGroupByLocation(v => !v)} color="#334155">Grouper par lieu</Chip>
              </MobileToolbar>
            )}
            {uniqueLocations.length > 1 && (
              <div className="mobile-hidden flex flex-wrap items-center gap-2">
                <button onClick={() => setFilter('all')}
                  className={cn('px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
                    filterLocation === 'all' ? 'bg-slate-700 text-white' : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50')}
                  data-testid="filter-location-all">
                  Tous les lieux
                </button>
                {uniqueLocations.map(loc => (
                  <button key={loc.id} onClick={() => setFilter(filterLocation === loc.id ? 'all' : loc.id)}
                    className={cn('px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
                      filterLocation === loc.id ? 'bg-[#0288D1] text-white' : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50')}
                    data-testid={`filter-location-${loc.id}`}>
                    <MapPin size={10} className="inline mr-1" />
                    {loc.venue_name}
                  </button>
                ))}
                <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer ml-2 select-none">
                  <input type="checkbox" checked={groupByLocation} onChange={e => setGroupByLocation(e.target.checked)}
                    className="rounded border-slate-300 text-[#0288D1]" />
                  <Layers size={11} />
                  Grouper par lieu
                </label>
              </div>
            )}

            {/* Cours actifs */}
            {activeCourses.length > 0 && renderGrid(activeCourses)}

            {/* Cours inactifs — accordéon replié par défaut */}
            {inactiveCourses.length > 0 && (
              <div id="inactive-courses" className="scroll-mt-20">
                <button
                  onClick={() => setInactivesOpen(v => !v)}
                  className="w-full flex items-center gap-3 mb-3 group"
                  data-testid="toggle-inactive-courses"
                >
                  <div className="h-px flex-1 bg-slate-200 group-hover:bg-slate-300 transition-colors" />
                  <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider group-hover:text-slate-600 transition-colors">
                    <ChevronDown size={12} className={cn('transition-transform duration-200', inactivesOpen ? 'rotate-180' : '')} />
                    Inactifs · {inactiveCourses.length}
                  </span>
                  <div className="h-px flex-1 bg-slate-200 group-hover:bg-slate-300 transition-colors" />
                </button>
                {inactivesOpen && renderGrid(inactiveCourses)}
              </div>
            )}
          </div>
        );
      })()}

      {showModal && (
        <GroupCourseModal
          locations={locations}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchCourses(); }}
        />
      )}

      {courses.length > 0 && <RevenuePivot courses={courses} filterLocation={filterLocation} />}
    </div>
  );
}

/* ─────────────────────────────────────────
   RevenuePivot — Tableau CA collectifs
───────────────────────────────────────── */
function RevenuePivot({ courses, filterLocation = 'all' }) {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState(() => new Set(pivotIds(courses)));  // défaut : cours actifs
  const [showInactiveRows, setShowInactiveRows] = useState(false);
  const [showInactiveFilters, setShowInactiveFilters] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [varPct, setVarPct] = useState(70);
  const [fixedCost, setFixedCost] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [savingCosts, setSavingCosts] = useState(false);
  const scrollRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPivot = useCallback(() => {
    if (selectedIds.size === 0) { setData(null); return; }
    setLoading(true);
    api.get(pivotUrl(selectedIds))  // v2.18 : même adresse que le préchargement
      .then(d => {
        setData(d);
        // Initialiser les paramètres depuis les valeurs sauvegardées
        if (d.var_cost_pct != null) setVarPct(d.var_cost_pct);
        if (d.fixed_cost_per_day != null) setFixedCost(d.fixed_cost_per_day);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedIds]);

  const saveCostParams = async () => {
    setSavingCosts(true);
    try {
      await api.put('/teacher', { var_cost_pct: varPct, fixed_cost_per_day: fixedCost });
      toast.success('Paramètres de coûts sauvegardés');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setSavingCosts(false); }
  };

  useEffect(() => { fetchPivot(); }, [fetchPivot]);

  // Rafraîchir quand la fenêtre reprend le focus (retour depuis GroupClassDetail)
  useEffect(() => {
    window.addEventListener('focus', fetchPivot);
    return () => window.removeEventListener('focus', fetchPivot);
  }, [fetchPivot]);

  const toggleCourse = (id) => setSelectedIds(ids => {
    const n = new Set(ids); n.has(id) ? n.delete(id) : n.add(id);
    localStorage.setItem('pivot_selected_ids', JSON.stringify([...n]));
    return n;
  });

  if (loading) return (
    <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-400 text-sm">Chargement...</div>
  );
  if (!data || data.dates.length === 0) return null;

  // Filtre de période
  const filteredDates = data.dates.filter(d =>
    (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo)
  );

  // Filtre lieu : cours à afficher dans les toggles et les lignes du tableau
  const locationCourses = filterLocation === 'all'
    ? courses
    : courses.filter(c => c.location_id === filterLocation);
  const locationCourseIds = new Set(locationCourses.map(c => c.id));

  // Séparer actifs et inactifs parmi les cours sélectionnés ET filtrés par lieu
  const activePivotCourses = data.courses.filter(c =>
    selectedIds.has(c.id) &&
    locationCourseIds.has(c.id) &&
    courses.find(o => o.id === c.id)?.is_active !== false
  );
  const inactivePivotCourses = data.courses.filter(c =>
    selectedIds.has(c.id) &&
    locationCourseIds.has(c.id) &&
    courses.find(o => o.id === c.id)?.is_active === false
  );
  const hasInactiveSelected = inactivePivotCourses.length > 0;

  // Totaux par date filtrés par lieu (remplace data.date_totals dans le tfoot)
  const locationDateTotals = Object.fromEntries(
    filteredDates.map(d => [
      d,
      [...activePivotCourses, ...inactivePivotCourses]
        .reduce((s, c) => s + (c.sessions[d]?.revenue || 0), 0)
    ])
  );

  // Totaux sur la période filtrée (scope lieu)
  const filteredCA = filteredDates.reduce((sum, d) => sum + (locationDateTotals[d] || 0), 0);
  const grandVarCost = Math.round(filteredCA * varPct / 100);
  const grandFixedCost = Math.round(fixedCost * filteredDates.length);
  const grandCosts = grandVarCost + grandFixedCost;
  const grandNet = filteredCA - grandCosts;
  const activeCA = filteredDates.reduce((sum, d) =>
    sum + activePivotCourses.reduce((s, c) => s + (c.sessions[d]?.revenue || 0), 0), 0);

  const renderCourseRow = (course, dimmed = false) => {
    const courseTotal = Object.values(course.sessions).filter(Boolean).reduce((s, x) => s + x.revenue, 0);
    return (
      <tr key={course.id}
        onClick={() => navigate(`/group-classes/${course.id}`)}
        className={cn('border-b border-slate-50 hover:bg-[#0288D1]/5 transition-colors cursor-pointer group', dimmed && 'opacity-60')}
        title={`Ouvrir ${course.name}`}>
        <td className="sticky left-0 z-10 bg-white group-hover:bg-[#0288D1]/5 transition-colors px-1.5 py-2 border-r border-slate-100" style={{ minWidth: 72, maxWidth: 72 }}>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: course.color || '#0288D1' }} />
            <span className="text-[10px] font-semibold text-slate-800 group-hover:text-[#0288D1] transition-colors leading-tight break-words">{course.name}</span>
          </div>
        </td>
        <td className="sticky z-10 bg-slate-50 group-hover:bg-[#0288D1]/5 transition-colors px-1.5 py-2.5 text-center border-r-2 border-slate-200" style={{ left: 72 }}>
          <span className="text-xs font-bold text-slate-800">{courseTotal > 0 ? `${courseTotal}.-` : '—'}</span>
        </td>
        {filteredDates.map(date => {
          const sess = course.sessions[date];
          if (sess === undefined) return <td key={date} className="px-2 py-2.5 text-center border-r border-slate-50"><span className="text-slate-200 text-xs">—</span></td>;
          if (sess === null) return <td key={date} className="px-2 py-2.5 text-center border-r border-slate-50 bg-slate-50/50"><span className="text-[9px] text-slate-300 font-semibold">ANNULÉ</span></td>;
          const p = sess.present;
          return (
            <td key={date} className="px-2 py-2.5 text-center border-r border-slate-50 last:border-r-0">
              <div className="text-sm font-bold text-slate-800">{sess.revenue > 0 ? `${sess.revenue}.-` : <span className="text-slate-300">—</span>}</div>
              <div className="flex justify-center gap-0.5 mt-0.5 flex-wrap">
                {p.leader > 0 && <span className="text-[8px] font-bold text-[#0288D1]">{p.leader}L</span>}
                {p.leader > 0 && (p.follower > 0 || p.trial > 0 || p.aide > 0) && <span className="text-[8px] text-slate-200">/</span>}
                {p.follower > 0 && <span className="text-[8px] font-bold text-pink-500">{p.follower}F</span>}
                {p.follower > 0 && (p.trial > 0 || p.aide > 0) && <span className="text-[8px] text-slate-200">/</span>}
                {p.trial > 0 && <span className="text-[8px] font-bold text-orange-500">{p.trial}E</span>}
                {p.trial > 0 && p.aide > 0 && <span className="text-[8px] text-slate-200">/</span>}
                {p.aide > 0 && <span className="text-[8px] font-bold text-purple-400">{p.aide}H</span>}
              </div>
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="mt-6 scroll-mt-16" id="revenue-pivot">
      {/* Header : titre + sélection cours + filtre période */}
      <div className="flex items-start justify-between mb-3 gap-3 flex-col sm:flex-row">
        <h2 className="font-bold text-slate-800 font-manrope flex items-center gap-2 shrink-0">
          <TrendingUp size={16} className="text-[#0288D1]" />
          Tableau CA collectifs
        </h2>
        <div className="flex flex-col gap-2 w-full sm:w-auto sm:items-end">
          {/* Sélection cours */}
          <div className="flex items-center gap-2 flex-wrap">
            {locationCourses.filter(c => c.is_active !== false).map(c => (
              <button key={c.id} onClick={() => toggleCourse(c.id)}
                data-testid={`pivot-toggle-${c.id}`}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all',
                  selectedIds.has(c.id) ? 'text-white border-transparent shadow-sm' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                )}
                style={selectedIds.has(c.id) ? { backgroundColor: c.color || '#0288D1' } : {}}>
                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: selectedIds.has(c.id) ? 'rgba(255,255,255,0.7)' : (c.color || '#0288D1') }} />
                {c.name}
              </button>
            ))}
            {locationCourses.filter(c => c.is_active === false).length > 0 && (
              <>
                <button
                  onClick={() => setShowInactiveFilters(v => !v)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-slate-400 border border-dashed border-slate-300 hover:bg-slate-50 transition-colors"
                  data-testid="toggle-inactive-filters">
                  {showInactiveFilters ? '↑ Masquer inactifs' : `· ${locationCourses.filter(c => c.is_active === false).length} inactif${locationCourses.filter(c => c.is_active === false).length > 1 ? 's' : ''} ▼`}
                </button>
                {showInactiveFilters && locationCourses.filter(c => c.is_active === false).map(c => (
                  <button key={c.id} onClick={() => toggleCourse(c.id)}
                    data-testid={`pivot-toggle-${c.id}`}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all',
                      selectedIds.has(c.id) ? 'bg-slate-500 text-white border-transparent' : 'bg-white border-slate-200 text-slate-300 hover:border-slate-400 hover:text-slate-500'
                    )}>
                    <div className="w-2 h-2 rounded-full shrink-0 bg-slate-300" />
                    {c.name}
                    <span className="text-[9px] opacity-60">inactif</span>
                  </button>
                ))}
              </>
            )}
          </div>
          {/* Filtre de période */}
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm w-full sm:w-auto">
            <span className="text-xs font-semibold text-slate-500 shrink-0">Période</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              data-testid="period-from"
              className="text-xs text-slate-700 bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer min-w-0 flex-1" />
            <span className="text-slate-300 text-xs shrink-0">→</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              data-testid="period-to"
              className="text-xs text-slate-700 bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer min-w-0 flex-1" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-slate-300 hover:text-slate-500 transition-colors ml-1 shrink-0" title="Réinitialiser">
                <X size={12} />
              </button>
            )}
            <span className="text-[10px] text-slate-400 ml-1 border-l border-slate-100 pl-2 shrink-0">
              {filteredDates.length} séance{filteredDates.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {filteredDates.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-400 text-sm">
          Aucune séance dans cette période
        </div>
      ) : (
      <div className="rounded-2xl border border-slate-100 shadow-sm" style={{ maxWidth: '100vw', overflow: 'hidden' }}>
        {/* Hint scroll mobile */}
        <div className="lg:hidden flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-100">
          <div className="flex gap-3 text-xs font-semibold text-slate-700">
            <span>CA total : <span className="text-[#0288D1]">{filteredCA}.-</span></span>
            <span>Net : <span className={grandNet >= 0 ? 'text-[#26A69A]' : 'text-red-500'}>{grandNet}.-</span></span>
          </div>
          <span className="text-[10px] text-slate-400">glisser →</span>
        </div>
        <div style={{ overflowX: 'scroll', WebkitOverflowScrolling: 'touch', width: '100%' }}>
          <table className="border-collapse w-full" style={{ minWidth: `${124 + filteredDates.length * 58}px` }}>
              <thead>
                <tr className="border-b-2 border-slate-100 bg-slate-50">
                  <th className="sticky left-0 z-20 bg-slate-50 px-2 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider border-r border-slate-100" style={{ minWidth: 72, maxWidth: 72 }}>
                    Cours
                  </th>
                  <th className="sticky z-20 bg-slate-100 px-2 py-3 text-center text-[10px] font-bold text-slate-600 uppercase tracking-wider border-r-2 border-slate-300" style={{ minWidth: 52, left: 72 }}>
                    Total
                  </th>
                  {filteredDates.map(date => (
                    <th key={date} className="px-1 py-3 text-center border-r border-slate-50 last:border-r-0" style={{ minWidth: 58 }}>
                      <div className="text-xs font-bold text-slate-700">
                        {format(parseISO(date), 'dd MMM', { locale: fr })}
                      </div>
                      <div className="text-[9px] text-slate-400 mt-0.5">L / F / E / H</div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {/* Cours actifs */}
                {activePivotCourses.map(course => renderCourseRow(course, false))}

                {/* Sous-total Actifs (si inactifs sélectionnés) */}
                {hasInactiveSelected && (
                  <tr className="border-t-2 border-slate-200 bg-slate-50/80">
                    <td className="sticky left-0 z-10 bg-slate-50/80 px-3 py-2 font-bold text-xs text-slate-600 uppercase tracking-wider border-r border-slate-100">
                      Total Actifs
                    </td>
                    <td className="sticky z-10 bg-slate-100 px-3 py-2 text-center border-r-2 border-slate-300" style={{ left: 72 }}>
                      <span className="text-sm font-extrabold text-slate-800">{activeCA > 0 ? `${activeCA}.-` : '—'}</span>
                    </td>
                    {filteredDates.map(date => {
                      const dayActive = activePivotCourses.reduce((s, c) => s + (c.sessions[date]?.revenue || 0), 0);
                      return <td key={date} className="px-2 py-2 text-center border-r border-slate-100"><span className="text-xs font-semibold text-slate-700">{dayActive > 0 ? `${dayActive}.-` : '—'}</span></td>;
                    })}
                  </tr>
                )}

                {/* Section Inactifs — repliable */}
                {hasInactiveSelected && (
                  <tr
                    onClick={() => setShowInactiveRows(v => !v)}
                    className="cursor-pointer border-t border-slate-200 bg-slate-100/60 hover:bg-slate-100 transition-colors select-none"
                    data-testid="toggle-inactive-rows">
                    <td colSpan={filteredDates.length + 2} className="sticky left-0 z-10 bg-slate-100/60 px-3 py-2 border-r-0">
                      <div className="flex items-center gap-2">
                        {showInactiveRows
                          ? <ChevronDown size={13} className="text-slate-400" />
                          : <ChevronRight size={13} className="text-slate-400" />}
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Inactifs</span>
                        <span className="text-xs text-slate-400">({inactivePivotCourses.length})</span>
                      </div>
                    </td>
                  </tr>
                )}
                {hasInactiveSelected && showInactiveRows && (
                  inactivePivotCourses.map(course => renderCourseRow(course, true))
                )}
              </tbody>

              <tfoot>
                {/* Total CA row */}
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td className="sticky left-0 z-10 bg-slate-50 px-3 py-2.5 font-bold text-sm text-slate-700 border-r border-slate-100">Total CA</td>
                  <td className="sticky z-10 bg-slate-100 px-3 py-2.5 text-center border-r-2 border-slate-300" style={{ left: 72 }}>
                    <span className="text-base font-extrabold text-slate-900">{filteredCA > 0 ? `${filteredCA}.-` : '—'}</span>
                  </td>
                  {filteredDates.map(date => (
                    <td key={date} className="px-2 py-2.5 text-center border-r border-slate-100 last:border-r-0">
                      <span className="text-sm font-bold text-slate-800">{(locationDateTotals[date] || 0) > 0 ? `${locationDateTotals[date]}.-` : '—'}</span>
                    </td>
                  ))}
                </tr>

                {/* Row 1 — Coûts variables (%) */}
                <tr className="bg-orange-50 border-t border-orange-100">
                  <td className="sticky left-0 z-10 bg-orange-50 px-3 py-2 border-r border-orange-100">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-orange-700">Coûts variables</span>
                      <input type="number" value={varPct} min={0} max={100}
                        onChange={e => setVarPct(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                        onBlur={saveCostParams}
                        data-testid="var-pct-input"
                        className="w-11 h-6 px-1 text-xs font-bold text-orange-700 bg-white border border-orange-300 rounded-lg text-center focus:outline-none focus:ring-1 focus:ring-orange-400"
                      />
                      <span className="text-xs font-bold text-orange-700">%</span>
                    </div>
                  </td>
                  <td className="sticky z-10 bg-orange-100 px-3 py-2 text-center border-r-2 border-orange-300" style={{ left: 72 }}>
                    <span className="text-sm font-extrabold text-orange-800">{grandVarCost > 0 ? `${grandVarCost}.-` : '—'}</span>
                  </td>
                  {filteredDates.map(date => {
                    const v = Math.round((locationDateTotals[date] || 0) * varPct / 100);
                    return <td key={date} className="px-2 py-2 text-center border-r border-orange-100 last:border-r-0">
                      <span className="text-xs font-semibold text-orange-700">{v > 0 ? `${v}.-` : '—'}</span>
                    </td>;
                  })}
                </tr>

                {/* Row 2 — Coûts fixes */}
                <tr className="bg-amber-50 border-t border-amber-100">
                  <td className="sticky left-0 z-10 bg-amber-50 px-3 py-2 border-r border-amber-100">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-amber-700">Coûts fixes</span>
                      <input type="number" value={fixedCost} min={0}
                        onChange={e => setFixedCost(Math.max(0, parseFloat(e.target.value) || 0))}
                        onBlur={saveCostParams}
                        data-testid="fixed-cost-input"
                        className="w-14 h-6 px-1 text-xs font-bold text-amber-700 bg-white border border-amber-300 rounded-lg text-center focus:outline-none focus:ring-1 focus:ring-amber-400"
                        placeholder="0"
                      />
                      <span className="text-xs font-bold text-amber-700">CHF/j</span>
                    </div>
                  </td>
                  <td className="sticky z-10 bg-amber-100 px-3 py-2 text-center border-r-2 border-amber-300" style={{ left: 72 }}>
                    <span className="text-sm font-extrabold text-amber-800">{grandFixedCost > 0 ? `${grandFixedCost}.-` : '—'}</span>
                  </td>
                  {filteredDates.map(date => (
                    <td key={date} className="px-2 py-2 text-center border-r border-amber-100 last:border-r-0">
                      <span className="text-xs font-semibold text-amber-700">{fixedCost > 0 ? `${fixedCost}.-` : '—'}</span>
                    </td>
                  ))}
                </tr>

                {/* Row 3 — Coûts totaux */}
                <tr className="bg-red-50 border-t-2 border-red-200">
                  <td className="sticky left-0 z-10 bg-red-50 px-3 py-2.5 font-bold text-sm text-red-700 border-r border-red-100">Coûts totaux</td>
                  <td className="sticky z-10 bg-red-100 px-3 py-2.5 text-center border-r-2 border-red-300" style={{ left: 72 }}>
                    <span className="text-base font-extrabold text-red-900">{grandCosts > 0 ? `${grandCosts}.-` : '—'}</span>
                  </td>
                  {filteredDates.map(date => {
                    const ca = locationDateTotals[date] || 0;
                    const total = Math.round(ca * varPct / 100) + Math.round(fixedCost);
                    return <td key={date} className="px-2 py-2.5 text-center border-r border-red-100 last:border-r-0">
                      <span className="text-xs font-bold text-red-700">{total > 0 ? `${total}.-` : '—'}</span>
                    </td>;
                  })}
                </tr>

                {/* Row 4 — Résultat net */}
                <tr className="bg-emerald-50 border-t-2 border-emerald-300">
                  <td className="sticky left-0 z-10 bg-emerald-50 px-3 py-2.5 font-bold text-sm text-emerald-800 border-r border-emerald-200">Résultat net</td>
                  <td className="sticky z-10 bg-emerald-100 px-3 py-2.5 text-center border-r-2 border-emerald-300" style={{ left: 72 }}>
                    <span className={cn('text-base font-extrabold', grandNet >= 0 ? 'text-emerald-900' : 'text-red-700')}>
                      {grandNet !== 0 ? `${grandNet}.-` : '—'}
                    </span>
                  </td>
                  {filteredDates.map(date => {
                    const ca = locationDateTotals[date] || 0;
                    const net = ca - Math.round(ca * varPct / 100) - Math.round(fixedCost);
                    return <td key={date} className="px-2 py-2.5 text-center border-r border-emerald-100 last:border-r-0">
                      <span className={cn('text-sm font-bold', net >= 0 ? 'text-emerald-700' : 'text-red-600')}>
                        {net !== 0 ? `${net}.-` : '—'}
                      </span>
                    </td>;
                  })}
                </tr>
              </tfoot>
            </table>
        </div>
      </div>
      )}
    </div>
  );
}


function GroupCourseModal({ locations, course, onClose, onSaved }) {
  const { t, lang } = useLang();
  const DAYS = lang === 'fr' ? DAYS_FR : DAYS_EN;
  const isEdit = !!course?.id;
  const [form, setForm] = useState({
    name: course?.name || '',
    description: course?.description || '',
    location_id: course?.location_id || (locations[0]?.id || ''),
    day_of_week: course?.day_of_week ?? 0,
    start_time: course?.start_time || '18:30',
    duration_minutes: course?.duration_minutes || 90,
    level: Array.isArray(course?.level) ? course.level : (course?.level ? [course.level] : []),
    color: course?.color || '#0288D1',
    price_per_class: course?.price_per_class ?? 25,
    start_date: course?.start_date || '',
    end_date: course?.end_date || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.location_id) { toast.error('Nom et lieu requis'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/group-courses/${course.id}`, form);
        toast.success('Cours mis à jour');
      } else {
        await api.post('/group-courses', form);
        toast.success('Cours créé');
      }
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    } finally { setSaving(false); }
  };

  const DURATIONS = [{ m: 60, l: '1h' }, { m: 90, l: '1h30' }, { m: 120, l: '2h' }];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="group-course-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h2 className="font-bold text-lg text-slate-900 font-manrope">
            {isEdit ? 'Modifier le cours' : 'Nouveau cours collectif'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          <Field label="Nom du cours *">
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
              placeholder="ex: Bachata Débutants" data-testid="course-name-input" />
          </Field>
          <Field label={t('groupClasses.level')}>
            <div className="flex flex-wrap gap-2">
              {LEVELS.map(l => (
                <button key={l} onClick={() => setForm(f => {
                  const next = f.level.includes(l) ? f.level.filter(x => x !== l) : [...f.level, l];
                  return { ...f, level: next };
                })}
                  className={cn('px-3 h-9 rounded-lg text-sm font-semibold transition-colors',
                    form.level.includes(l) ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                  {l}
                </button>
              ))}
            </div>
          </Field>
          <Field label={t('common.city') + ' / Salle'}>
            <select value={form.location_id} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}
              className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]">
              {locations.map(l => <option key={l.id} value={l.id}>{l.venue_name} · {l.city}</option>)}
            </select>
          </Field>
          <Field label={t('groupClasses.schedule')}>
            <div className="flex gap-1.5 flex-wrap mb-2">
              {DAYS.map((d, i) => (
                <button key={`day-${i}`} onClick={() => setForm(f => ({ ...f, day_of_week: i }))}
                  className={cn('px-3 h-9 rounded-lg text-sm font-bold transition-colors',
                    form.day_of_week === i ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                  {d}
                </button>
              ))}
            </div>
            <TimePicker24h value={form.start_time} onChange={v => setForm(f => ({ ...f, start_time: v }))} />
          </Field>
          <Field label="Durée">
            <div className="flex gap-2">
              {DURATIONS.map(d => (
                <button key={d.m} onClick={() => setForm(f => ({ ...f, duration_minutes: d.m }))}
                  className={cn('px-4 h-10 rounded-xl text-sm font-bold transition-colors',
                    form.duration_minutes === d.m ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                  {d.l}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Couleur de l'événement">
            <div className="flex flex-wrap gap-2">
              {COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                  className={cn('w-8 h-8 rounded-full transition-all border-2',
                    form.color === c ? 'border-slate-800 scale-110' : 'border-transparent hover:scale-105')}
                  style={{ backgroundColor: c }}
                  title={c}
                  data-testid={`color-btn-${c}`} />
              ))}
            </div>
          </Field>
          <Field label="Description (optionnel)">
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1] resize-none"
              placeholder="Informations sur le cours..." />
          </Field>
          <Field label="Prix par séance (CHF)">
            <div className="flex items-center gap-2">
              <input type="number" min="0" step="0.5" value={form.price_per_class}
                onChange={e => setForm(f => ({ ...f, price_per_class: parseFloat(e.target.value) || 0 }))}
                className="w-32 h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                data-testid="price-per-class-input" />
              <span className="text-sm text-slate-500">CHF / cours</span>
            </div>
          </Field>
          <Field label="Période d'activité">
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-1 flex-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Début</span>
                <input type="date" value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1] w-full"
                  data-testid="course-start-date" />
              </div>
              <span className="text-slate-400 mt-5">→</span>
              <div className="flex flex-col gap-1 flex-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Fin</span>
                <input type="date" value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1] w-full"
                  data-testid="course-end-date" />
              </div>
            </div>
            {(form.start_date || form.end_date) && (
              <p className="text-[10px] text-slate-400 mt-1.5">
                Seules les séances dans cette période apparaîtront dans le planning.
              </p>
            )}
          </Field>
        </div>
        <div className="px-5 pb-5 pt-3 border-t border-slate-100 flex gap-3 shrink-0">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">{t('common.cancel')}</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 h-12 rounded-xl bg-[#0288D1] text-white font-bold text-sm hover:bg-[#0277BE] transition-all"
            data-testid="save-course-btn">
            {saving ? '...' : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">{label}</label>
      {children}
    </div>
  );
}

export { GroupCourseModal };
